import React from 'react';
import { Icon } from '@iconify/react';

/**
 * Spinner - 統一的載入旋轉動畫組件
 * 使用 Tailwind animate-spin，不需要自訂 keyframes
 */

// 尺寸對照
const sizes = {
  xs: 'w-3 h-3',      // 12px - inline
  sm: 'w-4 h-4',      // 16px - button
  md: 'w-6 h-6',      // 24px - default
  lg: 'w-8 h-8',      // 32px
  xl: 'w-10 h-10',    // 40px
  '2xl': 'w-12 h-12', // 48px - fullscreen
};

// 邊框粗細
const borderWidths = {
  xs: 'border',
  sm: 'border-2',
  md: 'border-2',
  lg: 'border-[3px]',
  xl: 'border-[3px]',
  '2xl': 'border-4',
};

/**
 * 基礎 Spinner (圓形旋轉)
 */
export function Spinner({
  size = 'md',
  color = 'primary',
  className = ''
}) {
  const colorClasses = {
    primary: 'border-[var(--text-active)]',
    secondary: 'border-[var(--text-secondary)]',
    white: 'border-white',
    current: 'border-current',
  };

  return (
    <div
      className={`
        ${sizes[size]}
        ${borderWidths[size]}
        border-[var(--border-color)]
        ${colorClasses[color] || colorClasses.primary}
        border-t-current
        rounded-full
        animate-spin
        ${className}
      `}
      style={{ borderTopColor: color.startsWith('#') ? color : undefined }}
    />
  );
}

/**
 * Icon Spinner (使用 Iconify 的 loading icon)
 */
export function IconSpinner({
  size = 'md',
  color = 'current',
  className = ''
}) {
  const iconSizes = {
    xs: 12,
    sm: 16,
    md: 24,
    lg: 32,
    xl: 40,
    '2xl': 48,
  };

  return (
    <Icon
      icon="mdi:loading"
      width={iconSizes[size]}
      height={iconSizes[size]}
      className={`animate-spin text-${color} ${className}`}
    />
  );
}

/**
 * Spinner with Text
 */
export function SpinnerWithText({
  text = '載入中...',
  size = 'md',
  color = 'primary',
  direction = 'vertical', // 'vertical' | 'horizontal'
  className = ''
}) {
  return (
    <div className={`
      flex items-center justify-center gap-3 p-5
      ${direction === 'vertical' ? 'flex-col' : 'flex-row gap-2'}
      ${className}
    `}>
      <Spinner size={size} color={color} />
      <span className="text-xs text-[var(--text-secondary)] font-medium">
        {text}
      </span>
    </div>
  );
}

/**
 * Full Screen Loading (Fixed Overlay)
 */
export function FullScreenSpinner({
  text = '載入中...',
  className = ''
}) {
  return (
    <div className={`
      fixed inset-0 z-[9999]
      flex flex-col items-center justify-center
      bg-white/95
      ${className}
    `}>
      <Spinner size="2xl" color="primary" />
      <p className="mt-5 text-sm text-[var(--text-secondary)] font-medium">
        {text}
      </p>
    </div>
  );
}

/**
 * Page Loading - 頁面級載入狀態 (非 overlay，佔滿容器)
 */
export function PageSpinner({
  text = '載入中...',
  className = ''
}) {
  return (
    <div className={`
      min-h-screen bg-white
      flex flex-col items-center justify-center
      ${className}
    `}>
      <Spinner size="xl" color="primary" />
      {text && (
        <p className="mt-4 text-sm text-gray-500 font-medium">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * Card Loading - 卡片內載入狀態
 */
export function CardSpinner({
  text = '載入中...',
  minHeight = '200px',
  className = ''
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center p-10 ${className}`}
      style={{ minHeight }}
    >
      <Spinner size="xl" color="primary" />
      <p className="mt-4 text-xs text-[var(--text-secondary)] font-medium">
        {text}
      </p>
    </div>
  );
}

/**
 * Content Loading - 內容區塊載入狀態 (適合局部區域)
 */
export function ContentSpinner({
  text,
  className = ''
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-16 ${className}`}>
      <Spinner size="lg" color="primary" />
      {text && (
        <p className="mt-3 text-sm text-gray-500">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * Button Loading - 按鈕內載入
 */
export function ButtonSpinner({ color = 'white' }) {
  return <Spinner size="sm" color={color} />;
}

/**
 * Inline Loading - 行內小型載入
 */
export function InlineSpinner({ color = 'primary' }) {
  return <Spinner size="xs" color={color} />;
}

export default {
  Spinner,
  Icon: IconSpinner,
  WithText: SpinnerWithText,
  FullScreen: FullScreenSpinner,
  Page: PageSpinner,
  Card: CardSpinner,
  Content: ContentSpinner,
  Button: ButtonSpinner,
  Inline: InlineSpinner,
};
